import { isVersionedTransaction } from '@solana/wallet-adapter-base';
import { PublicKey, Transaction, VersionedTransaction } from '@solana/web3.js';
import { parseAccountId } from '@walletconnect/utils';
import base58 from 'bs58';
import { WalletConnectRPCMethods, WalletConnectChainID } from './constants';
import { getDefaultChainFromSession } from './utils';
import type UniversalProvider from '@walletconnect/universal-provider';

class ClientNotInitializedError extends Error {}
class WalletConnectFeatureNotSupportedError extends Error {
	constructor(method: WalletConnectRPCMethods) {
		super(`WalletConnect Adapter - Method ${method} is not supported by the wallet`);
		this.name = 'WalletConnectFeatureNotSupportedError';
	}
}

export class WalletConnectSolanaCore {
	private provider: UniversalProvider;
	private network: WalletConnectChainID = WalletConnectChainID.Mainnet;

	constructor(provider: UniversalProvider) {
		this.provider = provider;
		if (!this.session) throw new ClientNotInitializedError();
		this.network = getDefaultChainFromSession(this.session, this.network);
	}

	get session() {
		return this.provider.session;
	}

	get publicKey(): PublicKey {
		if (this.provider.session && this.session) {
			const { address } = parseAccountId(this.session?.namespaces['solana']?.accounts[0] ?? '');
			return new PublicKey(address);
		}
		throw new ClientNotInitializedError();
	}

	async signTransaction<T extends Transaction | VersionedTransaction>(transaction: T): Promise<T> {
		this.checkIfWalletSupportsMethod(WalletConnectRPCMethods.signTransaction);

		const isVersioned = isVersionedTransaction(transaction);
		const legacyTransaction = isVersioned ? {} : transaction;

		const { signature, transaction: signedSerializedTransaction } = await this.provider.client.request<{
			signature: string;
			transaction?: string;
		}>({
			chainId: this.network,
			topic: this.session!.topic,
			request: {
				method: WalletConnectRPCMethods.signTransaction,
				params: {
					...legacyTransaction,
					transaction: this.serialize(transaction),
				},
			},
		});

		if (signedSerializedTransaction) {
			return this.deserialize(signedSerializedTransaction, isVersioned) as T;
		}

		transaction.addSignature(this.publicKey, Buffer.from(base58.decode(signature)));
		return transaction;
	}

	async signMessage(message: Uint8Array): Promise<Uint8Array> {
		this.checkIfWalletSupportsMethod(WalletConnectRPCMethods.signMessage);

		const { signature } = await this.provider.client.request<{
			signature: string;
		}>({
			chainId: this.network,
			topic: this.session!.topic,
			request: {
				method: WalletConnectRPCMethods.signMessage,
				params: {
					pubkey: this.publicKey.toString(),
					message: base58.encode(message),
				},
			},
		});

		return base58.decode(signature);
	}

	async signAndSendTransaction<T extends Transaction | VersionedTransaction>(transaction: T): Promise<string> {
		this.checkIfWalletSupportsMethod(WalletConnectRPCMethods.signAndSendTransaction);

		const { signature } = await this.provider.client.request<{
			signature: string;
		}>({
			chainId: this.network,
			topic: this.session!.topic,
			request: {
				method: WalletConnectRPCMethods.signAndSendTransaction,
				params: { transaction: this.serialize(transaction) },
			},
		});

		return signature;
	}

	async signAllTransactions<T extends Transaction | VersionedTransaction>(transactions: T[]): Promise<T[]> {
		try {
			this.checkIfWalletSupportsMethod(WalletConnectRPCMethods.signAllTransactions);

			const serializedTransactions = transactions.map((transaction) => this.serialize(transaction));

			const { transactions: serializedSignedTransactions } = await this.provider.client.request<{
				transactions: string[];
			}>({
				chainId: this.network,
				topic: this.session!.topic,
				request: {
					method: WalletConnectRPCMethods.signAllTransactions,
					params: { transactions: serializedTransactions },
				},
			});

			return transactions.map((transaction, index) => {
				if (isVersionedTransaction(transaction)) {
					return this.deserialize(serializedSignedTransactions[index] ?? '', true);
				}
				return this.deserialize(serializedSignedTransactions[index] ?? '');
			}) as T[];
		} catch (error) {
			if (error instanceof WalletConnectFeatureNotSupportedError) {
				const promises = transactions.map((transaction) => this.signTransaction(transaction));
				const signedTransactions = await Promise.all(promises);
				return signedTransactions;
			}
			throw error;
		}
	}

	private serialize(transaction: Transaction | VersionedTransaction): string {
		return Buffer.from(transaction.serialize({ verifySignatures: false })).toString('base64');
	}

	private deserialize(serializedTransaction: string, versioned = false): Transaction | VersionedTransaction {
		if (versioned) {
			return VersionedTransaction.deserialize(Buffer.from(serializedTransaction, 'base64'));
		}
		return Transaction.from(Buffer.from(serializedTransaction, 'base64'));
	}

	private checkIfWalletSupportsMethod(method: WalletConnectRPCMethods) {
		if (!this.session?.namespaces['solana']?.methods.includes(method)) {
			throw new WalletConnectFeatureNotSupportedError(method);
		}
	}
}
